/**
 * NextMovesCards — Redesigned "NEXT MOVES" action stack for the Buyer Passport.
 *
 * Replaces the pill-chip strip (NextMovesStrip) with a card-per-action layout:
 * icon + title + subtitle, matching the design mockup's tall action cards.
 *
 * At low tiers (0/1): "START HERE" framing with action cards (connect bank,
 * verify income).
 * At high tier (2): a single "NEXT MOVE" card for lender pre-approval, shown as
 * coming soon — lender review isn't in-product yet, so the card points the buyer
 * at their own lender rather than implying an in-app invite that doesn't exist.
 *
 * Functional wiring:
 *   - Drives content from tier / verification signals and readiness goals.
 *   - onConnectBank / onUploadDocuments callbacks preserved.
 */

import { Banknote, FileText, Star } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { DashboardSnapshot } from "../../../lib/dashboard";
import type { ReadinessResult } from "../../../lib/readiness-types";
import { cn } from "../../../lib/utils";
import { deriveTier } from "./tier-derivation";
import type { PassportTier } from "./tier-derivation";

interface ActionCard {
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick?: () => void;
  href?: string;
  /** testid suffix */
  id: string;
  disabled?: boolean;
}

function ActionCardItem({ card }: { card: ActionCard }) {
  const inner = (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border border-border bg-primary-soft/40 px-4 py-3",
        "transition-colors",
        card.onClick || card.href
          ? "cursor-pointer hover:border-primary/30 hover:bg-primary-soft/70"
          : "",
        card.disabled && "cursor-default opacity-50",
      )}
      data-testid={`next-move-card-${card.id}`}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card text-primary">
        {card.icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold leading-tight text-foreground">
          {card.title}
        </p>
        <p className="mt-0.5 text-xs leading-4 text-muted-foreground">
          {card.subtitle}
        </p>
      </div>
    </div>
  );

  if (card.href && !card.disabled) {
    return <Link to={card.href}>{inner}</Link>;
  }

  if (card.onClick && !card.disabled) {
    return (
      <button type="button" className="w-full text-left" onClick={card.onClick}>
        {inner}
      </button>
    );
  }

  return inner;
}

export interface NextMovesCardsProps {
  snapshot: DashboardSnapshot;
  readiness: ReadinessResult | null | undefined;
  onConnectBank?: () => void;
  onUploadDocuments?: () => void;
  getToken?: () => Promise<string | null>;
}

export function NextMovesCards({
  snapshot,
  onConnectBank,
  onUploadDocuments,
  getToken,
}: NextMovesCardsProps) {
  const tier = deriveTier(snapshot);
  const hasBank = snapshot.hasPlaidItem;
  const hasDocs = snapshot.hasUploadedDocument;
  const hasCredit = snapshot.hasCreditScore;

  const cards = buildActionCards({
    tier,
    hasBank,
    hasDocs,
    hasCredit,
    onConnectBank: getToken ? onConnectBank : undefined,
    onUploadDocuments,
  });

  if (cards.length === 0) return null;

  const isHighTier = tier === 2;
  const eyebrow = isHighTier ? "NEXT MOVE" : "NEXT MOVES · START HERE";

  return (
    <div data-testid="next-moves-cards">
      <p className="mb-3 text-[0.625rem] font-semibold uppercase leading-none tracking-[0.22em] text-muted-foreground">
        {eyebrow}
      </p>
      <div className="flex flex-col gap-2">
        {cards.map((card) => (
          <ActionCardItem key={card.id} card={card} />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card derivation (pure, testable)
// ─────────────────────────────────────────────────────────────────────────────

interface BuildInput {
  tier: PassportTier;
  hasBank: boolean;
  hasDocs: boolean;
  hasCredit: boolean;
  onConnectBank?: () => void;
  onUploadDocuments?: () => void;
}

function buildActionCards({
  tier,
  hasBank,
  hasDocs,
  hasCredit,
  onConnectBank,
  onUploadDocuments,
}: BuildInput): ActionCard[] {
  // For a securely verified profile, offer a practical next step without
  // implying that lender verification is another marketplace tier.
  if (tier === 2) {
    return [
      {
        id: "lender-pre-approval",
        icon: <Star className="h-4 w-4" strokeWidth={2} />,
        title: "Get pre-approved with a lender",
        subtitle: "If you're planning to buy soon, ask your lender about pre-approval.",
      },
    ];
  }

  // Tier 0/1 — build the action stack based on what's missing.
  const cards: ActionCard[] = [];

  // Connect bank (highest priority if not connected)
  if (!hasBank) {
    cards.push({
      id: "connect-bank",
      icon: <Banknote className="h-4 w-4" strokeWidth={2} />,
      title: "Connect your bank",
      subtitle: hasCredit
        ? "Verifies cash, debt & credit"
        : "Unlocks your score",
      onClick: onConnectBank,
      disabled: !onConnectBank,
    });
  }

  // Verify income via docs if no docs yet and not bank-verified
  if (!hasDocs) {
    cards.push({
      id: "verify-income",
      icon: <FileText className="h-4 w-4" strokeWidth={2} />,
      title: "Verify income",
      subtitle: "Upload a W-2 or connect payroll",
      onClick: onUploadDocuments,
      disabled: !onUploadDocuments,
    });
  }

  // Cap at 3 cards for the design layout
  return cards.slice(0, 3);
}
